/**
 * NavigatorX Fraud Detection Dashboard
 * 
 * Dashboard for monitoring and managing fraud alerts:
 * - Same device detection
 * - Same IP detection
 * - Velocity checks
 * - Suspicious patterns
 * - Alert management
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Shield,
  AlertTriangle,
  Smartphone,
  Globe,
  Zap,
  Eye,
  RefreshCw,
  Loader2,
  CheckCircle,
  Users,
  Clock,
  ChevronDown,
  ChevronRight,
  Play,
  Link,
  UserX
} from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../../../utils/firebase/client';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { navDb } from '../../../utils/firebase/client';
import {
  getFraudStats,
  getFraudAlerts,
  runFraudScan,
  detectSameDevice,
  detectSameIP,
  detectVelocityAnomalies,
  detectSuspiciousPatterns,
  type FraudAlert,
  type FraudStats,
  type DeviceCluster,
  type IPCluster,
  type VelocityCheck
} from '../../../utils/services/navigatorXFraudService';
import type { NavUser } from '../../../types/navigatorx';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

interface FraudDetectionDashboardProps {
  embedded?: boolean;
}

export default function FraudDetectionDashboard({ embedded = false }: FraudDetectionDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [stats, setStats] = useState<FraudStats | null>(null);
  const [alerts, setAlerts] = useState<FraudAlert[]>([]);
  const [deviceClusters, setDeviceClusters] = useState<DeviceCluster[]>([]);
  const [ipClusters, setIPClusters] = useState<IPCluster[]>([]);
  const [velocityAnomalies, setVelocityAnomalies] = useState<VelocityCheck[]>([]);
  const [suspiciousPatterns, setSuspiciousPatterns] = useState<{
    selfReferrals: NavUser[];
    circularChains: { users: string[]; pattern: string }[];
    bulkSignups: { time: Date; count: number; users: NavUser[] }[];
  } | null>(null);
  const [expandedAlert, setExpandedAlert] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unresolved'>('unresolved');

  const adminId = auth.currentUser?.uid || 'unknown';

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, alertsData, devices, ips, velocity, patterns] = await Promise.all([
        getFraudStats(),
        getFraudAlerts(filter === 'unresolved'),
        detectSameDevice(),
        detectSameIP(),
        detectVelocityAnomalies(),
        detectSuspiciousPatterns()
      ]);

      setStats(statsData);
      setAlerts(alertsData);
      setDeviceClusters(devices);
      setIPClusters(ips);
      setVelocityAnomalies(velocity);
      setSuspiciousPatterns(patterns);
    } catch (error) {
      console.error('Error loading fraud data:', error);
      toast.error('Failed to load fraud data');
    } finally {
      setLoading(false);
    }
  };

  const handleRunScan = async () => {
    setScanning(true);
    try {
      const results = await runFraudScan();
      toast.success(
        `Scan complete: ${results.alertsCreated} new alerts created`,
        { description: `Found ${results.deviceClusters} device clusters, ${results.ipClusters} IP clusters` }
      );
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setScanning(false);
    }
  };

  const handleResolveAlert = async (alertId: string) => {
    try {
      await updateDoc(doc(navDb, 'nav_fraud_alerts', alertId), {
        resolved: true,
        resolvedAt: Timestamp.now(),
        resolvedBy: adminId
      });
      toast.success('Alert resolved');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'same_device': return <Smartphone className="h-4 w-4" />;
      case 'same_ip': return <Globe className="h-4 w-4" />;
      case 'velocity': return <Zap className="h-4 w-4" />;
      case 'pattern': return <Eye className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Fraud Detection</h2>
            <p className="text-gray-500">Monitor and manage referral fraud</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleRunScan} disabled={scanning} className="bg-red-500 hover:bg-red-600">
              {scanning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Scan
            </Button>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className={stats?.unresolvedAlerts ? 'border-red-200' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Unresolved Alerts</p>
                <p className={`text-2xl font-bold ${stats?.unresolvedAlerts ? 'text-red-600' : 'text-gray-700'}`}>
                  {stats?.unresolvedAlerts || 0}
                </p>
              </div>
              <AlertTriangle className={`h-8 w-8 ${stats?.unresolvedAlerts ? 'text-red-300' : 'text-gray-300'}`} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Device Clusters</p>
                <p className="text-2xl font-bold text-gray-700">{deviceClusters.length}</p>
                <p className="text-xs text-orange-500">{deviceClusters.filter(c => c.isReferralChain).length} suspicious</p>
              </div>
              <Smartphone className="h-8 w-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">IP Clusters</p>
                <p className="text-2xl font-bold text-gray-700">{ipClusters.length}</p>
                <p className="text-xs text-orange-500">{ipClusters.filter(c => c.isReferralChain).length} suspicious</p>
              </div>
              <Globe className="h-8 w-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Velocity Anomalies</p>
                <p className="text-2xl font-bold text-gray-700">{velocityAnomalies.length}</p>
              </div>
              <Zap className="h-8 w-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Breakdown */}
      {stats && (stats.alertsByType || stats.alertsBySeverity) && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Alerts by Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(stats.alertsByType || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(type)}
                      <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
                    </div>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
                {Object.keys(stats.alertsByType || {}).length === 0 && (
                  <p className="text-gray-500 text-sm">No alerts</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Alerts by Severity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {['critical', 'high', 'medium', 'low'].map(severity => {
                  const count = stats.alertsBySeverity?.[severity] || 0;
                  if (count === 0) return null;
                  return (
                    <div key={severity} className="flex items-center justify-between">
                      <Badge className={getSeverityColor(severity)}>{severity}</Badge>
                      <span className="font-medium">{count}</span>
                    </div>
                  );
                })}
                {Object.keys(stats.alertsBySeverity || {}).length === 0 && (
                  <p className="text-gray-500 text-sm">No alerts</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alert List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Fraud Alerts
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant={filter === 'unresolved' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('unresolved')}
              >
                Unresolved
              </Button>
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-3" />
              <p className="text-gray-500">No {filter === 'unresolved' ? 'unresolved ' : ''}alerts found</p>
              <p className="text-sm text-gray-400 mt-1">Run a scan to check for potential fraud</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map(alert => (
                <div
                  key={alert.id}
                  className={`border rounded-lg p-4 ${alert.resolved ? 'bg-gray-50' : 'bg-white'}`}
                >
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedAlert(expandedAlert === alert.id ? null : alert.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${getSeverityColor(alert.severity)}`}>
                        {getTypeIcon(alert.type)}
                      </div>
                      <div>
                        <p className="font-medium">{alert.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {alert.type.replace('_', ' ')}
                          </Badge>
                          <Badge className={`text-xs ${getSeverityColor(alert.severity)}`}>
                            {alert.severity}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {alert.detectedAt instanceof Timestamp 
                              ? alert.detectedAt.toDate().toLocaleString()
                              : 'Unknown'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {alert.resolved ? (
                        <Badge className="bg-green-100 text-green-800">Resolved</Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleResolveAlert(alert.id); }}
                          className="text-green-600"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Resolve
                        </Button>
                      )}
                      {expandedAlert === alert.id ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedAlert === alert.id && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500 mb-1">Affected Users</p>
                          <div className="space-y-1">
                            {alert.affectedUsers.map(userId => (
                              <code key={userId} className="block bg-gray-100 px-2 py-1 rounded text-xs">
                                {userId}
                              </code>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-gray-500 mb-1">Metadata</p>
                          <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                            {JSON.stringify(alert.metadata, null, 2)}
                          </pre>
                        </div>
                      </div>
                      {alert.resolved && (
                        <div className="mt-3 p-2 bg-green-50 rounded text-sm">
                          <p className="text-green-700">
                            Resolved by {alert.resolvedBy} at{' '}
                            {alert.resolvedAt instanceof Timestamp 
                              ? alert.resolvedAt.toDate().toLocaleString()
                              : 'Unknown'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Device Clusters */}
      {deviceClusters.filter(c => c.isReferralChain).length > 0 && (
        <Card className="mt-6 border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <Smartphone className="h-5 w-5" />
              Suspicious Device Clusters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {deviceClusters.filter(c => c.isReferralChain).map((cluster, i) => (
                <div key={i} className="p-3 bg-orange-50 rounded-lg">
                  <p className="font-medium text-orange-800">
                    Device: {cluster.deviceId.substring(0, 12)}...
                  </p>
                  <p className="text-sm text-orange-600 mt-1">
                    {cluster.users.length} accounts (referral chain detected)
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {cluster.users.map(user => (
                      <Badge key={user.id} variant="outline" className="text-xs">
                        {user.displayName || user.email || user.id.substring(0, 8)}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* IP Clusters */}
      {ipClusters.filter(c => c.isReferralChain).length > 0 && (
        <Card className="mt-6 border-purple-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <Globe className="h-5 w-5" />
              Suspicious IP Clusters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {ipClusters.filter(c => c.isReferralChain).map((cluster, i) => (
                <div key={i} className="p-3 bg-purple-50 rounded-lg">
                  <p className="font-medium text-purple-800">
                    IP: {cluster.ipAddress}
                  </p>
                  <p className="text-sm text-purple-600 mt-1">
                    {cluster.users.length} accounts (referral chain detected)
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {cluster.users.map(user => (
                      <Badge key={user.id} variant="outline" className="text-xs">
                        {user.displayName || user.email || user.id.substring(0, 8)}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Velocity Anomalies */}
      {velocityAnomalies.length > 0 && (
        <Card className="mt-6 border-yellow-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700">
              <Zap className="h-5 w-5" />
              Velocity Anomalies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {velocityAnomalies.map((anomaly, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div>
                    <code className="text-sm">{anomaly.userId}</code>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-yellow-800">{anomaly.referralCount} referrals</p>
                    <p className="text-xs text-yellow-600">in {anomaly.timeWindow}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Self-Referrals */}
      {suspiciousPatterns && suspiciousPatterns.selfReferrals.length > 0 && (
        <Card className="mt-6 border-red-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <UserX className="h-5 w-5" />
              Self-Referrals
              <Badge className="bg-red-100 text-red-800 ml-2">{suspiciousPatterns.selfReferrals.length}</Badge>
            </CardTitle>
            <p className="text-sm text-red-600">Users who referred themselves — critical fraud indicator</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {suspiciousPatterns.selfReferrals.map(user => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-red-800">
                        {user.displayName || user.email || 'Unknown User'}
                      </p>
                      <code className="text-xs text-red-500">{user.id}</code>
                    </div>
                  </div>
                  <Badge className="bg-red-100 text-red-800 border-red-200">Critical</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Circular Referral Chains */}
      {suspiciousPatterns && suspiciousPatterns.circularChains.length > 0 && (
        <Card className="mt-6 border-red-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Link className="h-5 w-5" />
              Circular Referral Chains
              <Badge className="bg-red-100 text-red-800 ml-2">{suspiciousPatterns.circularChains.length}</Badge>
            </CardTitle>
            <p className="text-sm text-red-600">Users referring each other in loops to farm free months</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {suspiciousPatterns.circularChains.map((chain, i) => (
                <div key={i} className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-100 rounded-lg mt-0.5">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-mono text-sm text-red-800 break-all">{chain.pattern}</p>
                      <p className="text-xs text-red-500 mt-1">{chain.users.length} users in chain</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {chain.users.map((userId, j) => (
                          <code key={j} className="text-xs bg-red-100 px-2 py-0.5 rounded">
                            {userId.substring(0, 12)}...
                          </code>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Signups */}
      {suspiciousPatterns && suspiciousPatterns.bulkSignups.length > 0 && (
        <Card className="mt-6 border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Users className="h-5 w-5" />
              Bulk Signup Clusters
              <Badge className="bg-amber-100 text-amber-800 ml-2">{suspiciousPatterns.bulkSignups.length}</Badge>
            </CardTitle>
            <p className="text-sm text-amber-600">5+ accounts created within a 5-minute window</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {suspiciousPatterns.bulkSignups.map((cluster, i) => (
                <div key={i} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-600" />
                      <p className="font-medium text-amber-800">
                        {cluster.time.toLocaleString()}
                      </p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                      {cluster.count} accounts
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {cluster.users.map(user => (
                      <div key={user.id} className="flex items-center gap-2 p-2 bg-white rounded border border-amber-100">
                        <div className="w-2 h-2 rounded-full bg-amber-400" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {user.displayName || user.email || 'Unknown'}
                          </p>
                          <code className="text-xs text-gray-400">{user.id.substring(0, 12)}...</code>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
